"use client";

import { cn } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "../ui/badge";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Progress } from "@/components/ui/progress";
import builder from "@builder.io/react";
import { Skeleton } from "../ui/skeleton";
import { MagicCard } from "@/components/magicui/magic-card";

interface useCases {
  title?: string;
  url?: string;
  description?: string;
}

interface Feature22Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  removePaddingBottom?: boolean;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature22 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 22",
  removePaddingBottom = false,
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  attributes,
}: Feature22Props) => {

  const ItemBlock = ({item}: {item: {title?: string, url?: string, description?: string}}) => {
    return (
      <a href={item.url ?? ""} className={cn("rounded-2xl flex-1 h-full shadow-none border-none flex relative  group")}>
        <MagicCard
          gradientColor={darkMode ? "#fffffff" : "#fffffff"}
          className="p-0"
        >
         <div className="flex flex-col gap-20  rounded-2xl p-6 flex-1 h-full justify-between">
            <div className="flex items-center justify-between gap-10">
              {item.title && (
                <span className="paragraph-lg font-bold">{parse(item.title)}</span>
              )}
              <Button variant="outline" size="iconLg" grayBorder lifted className="hidden lg:block">
                <span className="material-symbols-outlined">
                  arrow_forward
                </span>
              </Button>
            </div>
            <div className="flex flex-col gap-6">
              {item.description && (
                <div className="text-muted-foreground">
                  {parse(item.description)}
                </div>
              )}
              <Button variant="outline" size="iconLg" grayBorder lifted className="block lg:hidden">
                <span className="material-symbols-outlined">
                  arrow_forward
                </span>
              </Button>
            </div>
          </div>
          </MagicCard> 
      </a>
    );  
  }

  const ItemBlockSkeleton = () => {
    return (
      <div className={cn("rounded-2xl border border-border flex-1 h-full flex")}>
        <div className="flex flex-col gap-20 bg-background rounded-2xl p-6 flex-1 h-full justify-between">
          <div className="flex items-center justify-between gap-10">
            <Skeleton className="h-6 w-[70%] bg-muted rounded"></Skeleton>
            <Skeleton className="h-11 w-11 bg-muted rounded-full"></Skeleton>
          </div>
          <div className="flex flex-col gap-2">
            <Skeleton className="h-6 w-full bg-muted rounded"></Skeleton>
            <Skeleton className="h-6 w-full bg-muted rounded"></Skeleton>
            <Skeleton className="h-6 w-full bg-muted rounded"></Skeleton>
          </div>
        </div>
      </div>
    )
  }

  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [useCasesData, setUseCasesData] = useState<useCases[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!carouselApi || useCasesData.length === 0) {
      return;
    }

    const handleScroll = ({ scrollProgress }: { scrollProgress: () => number }) => {
      setProgress(
        Math.max(1 / useCasesData.length, Math.min(1, scrollProgress())) * 100,
      );
    };

    // Set initial progress
    handleScroll({ scrollProgress: () => carouselApi.scrollProgress() });

    carouselApi.on("scroll", handleScroll);
    carouselApi.on("reInit", handleScroll);

    return () => {
      carouselApi.off("scroll", handleScroll);
      carouselApi.off("reInit", handleScroll);
    };
  }, [carouselApi, useCasesData.length]);

  useEffect(() => {
    const fetchUseCases = async () => {
      setIsLoading(true);
      try {
        const [data] = await Promise.all([
          builder.getAll('use-cases', {
            limit: 8,
            offset: 0,
            query: {
              'data.title': { $ne: 'Business Use Cases for Working Capital Solutions' }
            },
            options: {
              noTargeting: true,
            },
            preview: true,
            noTraverse: true,
            noEditorUpdates: true,
            prerender: false,
          }),
        ]);
        
        const formattedData = data.map(item => ({
          title: item.data?.title || '',
          url: item.data?.url || '',
          description: item.data?.description || ''
        }));
        
        setUseCasesData(formattedData);
      } catch (err) {
        console.error('Error fetching use cases:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUseCases();
  }, []);

  const gradientBackground = cn(
    "bg-gradient-to-t",
    variant === 'A' 
      ? "from-background via-background/80 to-background/60" 
      : variant === 'B' 
        ? "from-muted via-muted/80 to-muted/60"
        : "from-background via-background/80 to-background/60" 
  );

  // const LoadingSkeletonBackground = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      removePaddingBottom={removePaddingBottom}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature22"
      attributes={attributes}
    >

      <div className="container">
        <div className="flex flex-col justify-between gap-6 items-center text-center" data-aos="fade-up">
          {title && (
            <> 
              {badge && (
                <Badge variant="outline" bold>
                  {badge}
                </Badge>
              )}
              <h2>
                {parse(title)}
              </h2>
            </>
          )}
          {(content || hasButton) && (
            <>  
              {content && (
                <div className="text-muted-foreground">
                  {parse(content)}
                </div>
              )}
              {hasButton && (
                <div className="flex flex-col md:flex-row gap-4 mt-4 lg:hidden w-full justify-center">
                  <div className="flex flex-col">
                    <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={buttonLink ?? ""}>
                        {buttonText}
                      </Link>
                    </Button>
                    {microCopy && (
                      <p className="paragraph-sm text-muted-foreground text-center mt-3">
                        {microCopy}
                      </p>
                    )}
                  </div>
                  {secondaryButton && (
                    <Button asChild variant={secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={secondaryButtonLink ?? ""}>
                        {secondaryButtonText}
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {isLoading ? (
          <> 
          <div className="mt-14 lg:mt-20 gap-6 md:grid-cols-2 hidden lg:grid" data-aos="fade-up" data-aos-delay={100}>
              <ItemBlockSkeleton />
              <ItemBlockSkeleton /> 
              <div className="grid gap-6 md:col-span-2 md:grid-cols-3">
                <ItemBlockSkeleton />
                <ItemBlockSkeleton /> 
                <ItemBlockSkeleton /> 
              </div>
            </div>
          </>
        ) : (
          <div className="mt-14 lg:mt-20 gap-6 md:grid-cols-2 hidden lg:grid" data-aos="fade-up" data-aos-delay={100}>
            {useCasesData && useCasesData?.slice(0, 2).map((item, index) => (  
              <ItemBlock key={index} item={item} />
            ))}

            {useCasesData && useCasesData?.length > 2 && (
              <div className="grid gap-6 md:col-span-2 md:grid-cols-3">
                {useCasesData?.slice(2, 5).map((item, index) => (
                  <ItemBlock key={index} item={item} />
                ))}
              </div>
            )}
            {useCasesData && useCasesData?.length > 5 && (
              <div className="grid gap-6 md:col-span-2 md:grid-cols-3 relative">
                {useCasesData?.slice(5, 8).map((item, index) => (
                  <ItemBlock key={index} item={item} />
                ))}

                <div className={cn("absolute inset-0 pointer-events-none", gradientBackground)}></div>
                <div className="hidden gap-6 lg:flex lg:flex-col absolute bottom-0 left-0 py-16 w-full items-center z-10">
                  {hasButton && (
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex flex-col">
                        <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                          <Link href={buttonLink ?? ""}>
                            {buttonText}
                          </Link>
                        </Button>
                        {microCopy && (
                          <p className="paragraph-sm text-muted-foreground text-center mt-3">
                            {microCopy}
                          </p>
                        )}
                      </div>
                      {secondaryButton && (
                        <Button asChild variant={secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                          <Link href={secondaryButtonLink ?? ""}>
                            {secondaryButtonText}
                          </Link>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
      </div>
      <div className="relative w-full overflow-hidden mt-14 lg:mt-20 block lg:hidden" >
        <Carousel 
          setApi={setCarouselApi}
          opts={{
            align: "start",
            breakpoints: {
              "(max-width: 768px)": {
                dragFree: true,
              },
            },
          }}
          data-aos="fade-left" data-aos-delay={100}
        >
          <CarouselContent 
            className={cn(
              "mr-4 ml-0",
              "md:mr-8 md:ml-3",
            )}
          >
            {isLoading ? (
              // Show 5 skeleton items for mobile carousel loading
              Array.from({ length: 5 }).map((_, index) => (
                <CarouselItem 
                  key={index} 
                  className="pl-4 md:pl-5 max-w-[344px] grid-cols-1"
                >
                  <div className="flex flex-1 h-full p-[1px]">
                    <ItemBlockSkeleton />
                  </div>
                </CarouselItem>
              ))
            ) : (
              useCasesData?.map((item, index) => (
                <CarouselItem 
                  key={index} 
                  className="pl-4 md:pl-5 max-w-[344px] grid-cols-1"
                >
                  <div className="flex flex-1 h-full p-[1px]">
                    <a href={item.url ?? ""} className={cn("rounded-2xl border border-border hover:border-brand flex-1 h-full flex")}>
                      <div className="flex flex-col gap-20 bg-background rounded-2xl p-6 flex-1 h-full justify-between">
                        <div className="flex items-center justify-between gap-10">
                          {item.title && (
                            <span className="paragraph-lg font-bold">{parse(item.title)}</span>
                          )}
                          <Button variant="outline" size="iconLg" grayBorder lifted className="hidden lg:block">
                            <span className="material-symbols-outlined">
                              arrow_forward
                            </span>
                          </Button>
                        </div>
                        <div className="flex flex-col gap-6">
                          {item.description && (
                            <div className="text-muted-foreground">
                              {parse(item.description)}
                            </div>
                          )}
                          <Button variant="outline" size="iconLg" grayBorder lifted className="block lg:hidden">
                            <span className="material-symbols-outlined">
                              arrow_forward
                            </span>
                          </Button>
                        </div>
                      </div>
                    </a>
                  </div>
                </CarouselItem>
              ))
            )}
          </CarouselContent>
          <div className="container mt-6">
            <Progress value={progress} className="h-[2px] w-full max-w-[300px]" />
          </div>
        </Carousel>
      </div>
    </SectionWrapper>
  )
};

export { Feature22 };